import React, { Fragment } from 'react';
import { Container, Pagination } from 'reactstrap';
import dayjs from 'dayjs';
import { Dialog, Loading } from '@/components';
import { States } from '@/helpers/common/states';
import Page from '@/model/Page';
import { ProducerProductLink } from '@/model/ProducerProductLinkDTO';
import { ActionProps } from '@/components/Dialog';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import FilterVector from '@/assets/images/svg/FilterVector';
import { CustomTable } from '@/components/Table';
import { updateMask } from '@/helpers/masks/cpf';
import { ReactComponent as Detail } from '@/assets/images/svg/detail.svg';
import { ReactComponent as Transfer } from '@/assets/images/svg/transfer.svg';
import { ReactComponent as X } from '@/assets/images/svg/x.svg';
import Event from '@/model/Event';
import Section from '@/model/Section';
import { FilterContent } from '../../../components/FilterContent';
import { columns, ShouldShowModal } from '../../../types';

interface StateProps {
  state: States;
  title: string | React.ReactNode;
  visible: boolean;
  shouldShowModal: ShouldShowModal;
  events: Event[];
  sections: Section[];
  formData: FormData;
  formErrors: FormErrors;
  currentPage: Page<ProducerProductLink, ProducerProductLink>;
}

interface DispatchProps {
  onToggle: () => void;
  onClearFilter: () => void;
  onFilter: () => void;
  onChangeFormInput: OnChangeFormInput;
  onGetSectionsByEvent: (eventId: string) => void;
  onShouldShowModal: (value: ShouldShowModal, newTitleModal: string | React.ReactNode) => void;
  onShowDetail: (id: string) => void;
  onValidate: (id: string) => void;
  onRefused: (id: string) => void;
  onPaginationChange: (page: number) => void;
}

export type Props = StateProps & DispatchProps;

export const ProducerProductContainer: React.FC<Props> = ({
  state,
  title,
  visible,
  onToggle,
  onClearFilter,
  onFilter,
  shouldShowModal,
  events,
  sections,
  formData,
  formErrors,
  onChangeFormInput,
  onGetSectionsByEvent,
  onShouldShowModal,
  currentPage,
  onShowDetail,
  onValidate,
  onRefused,
  onPaginationChange,
}): JSX.Element => {
  const renderActionDialogToFilter: ActionProps = {
    title: 'Limpar',
    onClick: (): void => onClearFilter(),
    theme: 'noneBorder',
  };
  const mountDataTable =
    currentPage && currentPage.list && currentPage.list.length > 0
      ? currentPage.list.map(data => ({
          eventName: data.event.name && data.event.name.trim().length > 0 ? data.event.name : '',
          sectionName:
            data.section.name && data.section.name.trim().length > 0 ? data.section.name : '',
          date: dayjs(data.createdAt).format('DD/MM/YYYY [às] HH:mm'),
          cpf: updateMask(data.cpf),
          actions: (
            <React.Fragment>
              <Detail
                className={'mr-4 svg-icon action-icon'}
                onClick={() => onShowDetail(data.id)}
              />
              <Transfer
                title="Validar"
                className={'mr-4 svg-icon-error action-icon'}
                onClick={() => onValidate(data.id)}
              />
              <X
                title="Recusar"
                className={'mr-4 svg-icon-error action-icon'}
                onClick={() => onRefused(data.id)}
              />
            </React.Fragment>
          ),
        }))
      : [];
  return (
    <Fragment>
      <Loading isVisible={state === States.loading} />
      <Dialog
        title={title}
        visible={visible}
        onClose={onToggle}
        position={'center'}
        isContentWithCard={true}
        actions={[
          {
            [ShouldShowModal.filter]: renderActionDialogToFilter,
          }[shouldShowModal],
          {
            [ShouldShowModal.filter]: {
              title: 'Filtrar',
              onClick: () => onFilter(),
            },
          }[shouldShowModal],
        ]}
      >
        {
          {
            [ShouldShowModal.filter]: (
              <FilterContent
                events={events}
                sections={sections}
                formData={formData}
                formErrors={formErrors}
                onChangeFormInput={onChangeFormInput}
                onGetSectionsByEvent={onGetSectionsByEvent}
              />
            ),
          }[shouldShowModal]
        }
      </Dialog>
      <Container className="mainContainer" fluid={true}>
        <div className="d-flex justify-content-between" style={{ paddingBottom: '30px' }}>
          <h5 className="pageTitle">Validar produtos</h5>
          <div className="button-filter-container">
            <div className="filter-container">
              <div
                className="filter-content"
                onClick={(): void => {
                  onShouldShowModal(ShouldShowModal.filter, 'Filtrar');
                }}
              >
                <FilterVector />
              </div>
            </div>
          </div>
        </div>
        <div style={{ overflowY: 'auto' }}>
          <CustomTable
            columns={columns}
            data={mountDataTable}
            numberRowsPerPage={currentPage.pageSize || 0}
            progressPending={state === States.loading}
          />
        </div>
        <Pagination
          currentPage={currentPage.page}
          totalCount={currentPage.total}
          pageSize={currentPage.pageSize}
          onPageChange={(page: number) => onPaginationChange(page)}
          total={currentPage.total}
        />
      </Container>
    </Fragment>
  );
};
